import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, CardMedia, CircularProgress, IconButton, List, ListItemButton, ListItemIcon, ListItemText, MenuItem, Modal, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { isAfter } from 'date-fns';
import { useFormik } from 'formik';
import * as React from 'react';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';
import Carousel from 'react-material-ui-carousel';
import { useDispatch } from 'react-redux';
import * as ProductHelper from '../../../api/product/productHelper';
import { drainageTrenchPD, drainHeight } from '../../../conversion/drainageTrench';
import { drainUnitLength, drainUnitWidth, hasDrainagePiping, maximumAppliedLoad, maxUnitaryDrainageCapacity, productPD } from '../../../conversion/product';
import { getLanguage } from '../../../localization';
import { cleanImage } from '../../../store/Image/imageSlice';
import { cleanAlert } from '../../../store/alert/alertSlice';
import { useAppSelector } from '../../../store/hook';
import { selectProductPreview } from '../../../store/product/productPreviewSlice';
import { selectProject, setDrainageTrenches } from '../../../store/project/projectSlice';
import { cleanResult } from '../../../store/result/resultSlice';
import { selectUser } from '../../../store/user/userSlice';
import DropDown from '../../component/DropDown';
import Input from '../../component/Input';
import { EDrainageTrenchType, ERole, EStatus } from '../../enum';
import { IDrainageTrench, IInputProductPhysicalDatas, IProduct, TInputDrainageTrench, TInputProductPhysicalDatas } from '../../iProject';
import variable from '../../style/variable';
import { dialog, toastAlert } from '../../util';

interface IModalProductProps {
    drainageTrenchType: EDrainageTrenchType;
    open: boolean;
    onClose: () => void;
}

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 830,
};

let currentProduct: IProduct;

const ModalProduct = (props: IModalProductProps) => {
    const baseProductPreview = useAppSelector(selectProductPreview);

    const project = useAppSelector(selectProject);
    const user = useAppSelector(selectUser);
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = React.useState(false);
    const [baseProduct, setBaseProduct] = React.useState<IProduct>();
    const [selectedIndex, setSelectedIndex] = React.useState<number>();
    const [drainHeightOpt, setDrainHeightOpt] = React.useState<number[]>();
    const [drainUnitWidthOpt, setDrainUnitWidthOpt] = React.useState<number[]>();

    const { t } = useTranslation();

    const drainChoiceType = [t('drainChoiceType', { type: t('primaryDrainageTrench') }), t('drainChoiceType', { type: t('secondaryDrainageTrench') })];

    const formik = useFormik<IInputProductPhysicalDatas>({
        initialValues: {},
        onSubmit: values => {},
    });

    React.useEffect(() => {
        switch (props.drainageTrenchType) {
            case EDrainageTrenchType.PrimaryDrainageTrench:
                currentProduct = project.data!.drainageTrenches![0]?.product!;
                break;
            case EDrainageTrenchType.SecondaryDrainageTrench:
                currentProduct = project.data!.drainageTrenches![1]?.product!;
                break;
            default:
                currentProduct = {};
                break;
        }
        if (currentProduct !== undefined) {
            setSelectedIndex(baseProductPreview.data!.findIndex(x => x.id == (currentProduct.isBase ? currentProduct.id : currentProduct.baseId)));
            formik.setValues(currentProduct.productPhysicalDatas![0]);
        }
    }, [props.open, JSON.stringify(project.data?.drainageTrenches)]);

    React.useEffect(() => {
        if (props.open) {
            if (baseProduct) {
                let drainHeightOpt: number[] = [];
                let drainUnitWidthOpt: number[] = [];
                if (baseProduct.baseId !== currentProduct.baseId) {
                    let newPhysicalData = baseProduct!.productPhysicalDatas![0];
                    formik.setValues(newPhysicalData);
                    baseProduct!.productPhysicalDatas!.map(x => {
                        drainHeightOpt!.push(x.drainHeight!);
                        if (x.drainHeight == newPhysicalData.drainHeight) drainUnitWidthOpt!.push(x.drainUnitWidth!);
                    });
                } else {
                    baseProduct!.productPhysicalDatas!.map(x => {
                        drainHeightOpt!.push(x.drainHeight!);
                        if (x.drainHeight == formik.values.drainHeight) drainUnitWidthOpt!.push(x.drainUnitWidth!);
                    });
                }
                setDrainHeightOpt([...new Set(drainHeightOpt)]);
                setDrainUnitWidthOpt([...new Set(drainUnitWidthOpt)]);
            } else {
                fetchBaseProduct(currentProduct.baseId!);
            }
        }
    }, [baseProduct, props.open]);

    const fetchBaseProduct = async (id: number) => {
        setIsLoading(true);
        let res = await ProductHelper.getProductByIdAsync(id);
        let baseProduct: IProduct = {
            ...res.data,
            productPhysicalDatas: res.data.productPhysicalDatas?.filter(x => x.unitOfMeasure == project.data?.projectInfo.unitOfMeasure),
        };
        setBaseProduct(baseProduct);
        setIsLoading(false);

        if (currentProduct.baseId == baseProduct.id) {
            handleObsoleteProduct(currentProduct.lastUpdateDate!, baseProduct.lastUpdateDate!);
        }
    };

    const handleObsoleteProduct = (updateDateCurrentProduct: Date, updateDateBaseProduct: Date) => {
        const cp = new Date(updateDateCurrentProduct);
        const bp = new Date(updateDateBaseProduct);

        if (props.open && isAfter(bp, cp)) {
            dialog('warning', t('obsoleteProduct', { product: currentProduct.name }));
        }
    };

    const handleListItemClick = (index: number, productId: number) => {
        setSelectedIndex(index);
        fetchBaseProduct(productId);
    };

    const handleClickApply = () => {
        let product: IProduct = { ...baseProduct, productPhysicalDatas: [{ ...baseProduct!.productPhysicalDatas![0], ...formik.values }] };
        let drainageTrenches: IDrainageTrench[] = [...project.data?.drainageTrenches!];
        switch (props.drainageTrenchType) {
            case EDrainageTrenchType.PrimaryDrainageTrench:
                drainageTrenches[0] = { ...drainageTrenches[0], product };
                break;
            case EDrainageTrenchType.SecondaryDrainageTrench:
                drainageTrenches[1] = { ...drainageTrenches[1], product };
                break;
            default:
                currentProduct = {};
                break;
        }
        dispatch(setDrainageTrenches(drainageTrenches));
        toastAlert('success', t('updatedProduct'));
        dispatch(cleanResult());
        dispatch(cleanImage());
        dispatch(cleanAlert());
    };

    const checkDrainHeight = (value: number) => {
        if (props.drainageTrenchType == EDrainageTrenchType.PrimaryDrainageTrench) {
            if (value < project.data?.drainageTrenches![EDrainageTrenchType.SecondaryDrainageTrench]?.product?.productPhysicalDatas![0].drainHeight!) {
                dialog('warning', t('increasePrimaryDrainHeight'));
            }
        } else {
            if (value > project.data?.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench].product?.productPhysicalDatas![0].drainHeight!) {
                dialog('warning', t('reduceSecondaryDrainHeight'));
            }
        }
    };

    const handleChangeDrainHeight = (event: SelectChangeEvent<any>) => {
        const { name, value } = event.target;
        checkDrainHeight(value);
        let currentDrainUnitWidth = formik.values.drainUnitWidth;
        let physicalDataByDrainHeight = baseProduct?.productPhysicalDatas?.filter(x => x.drainHeight == value)!;
        let newPhysicalData = physicalDataByDrainHeight?.find(x => x.drainUnitWidth == currentDrainUnitWidth) ?? physicalDataByDrainHeight[0];
        formik.setValues(newPhysicalData);

        let newDrainUnitWidthOpt = physicalDataByDrainHeight.map(x => x.drainUnitWidth!);
        setDrainUnitWidthOpt(newDrainUnitWidthOpt);
    };

    const handleChangeDrainUnitWidth = (event: SelectChangeEvent<any>) => {
        const { name, value } = event.target;
        let currentDrainHeight = formik.values.drainHeight;
        let newPhysicalData = baseProduct?.productPhysicalDatas?.find(x => x.drainHeight == currentDrainHeight && x.drainUnitWidth == value)!;
        formik.setValues(newPhysicalData);
    };

    const handleChange = (event: React.ChangeEvent<any> | SelectChangeEvent<any>) => {
        let { name, value } = event.target;
        let valueAsNumber = parseFloat(value);
        let updateValue = { [name]: valueAsNumber };
        if (!isNaN(valueAsNumber)) {
            let payload: IInputProductPhysicalDatas = { ...formik.values, ...updateValue };
            formik.setValues(payload);
            dispatch(cleanResult());
            dispatch(cleanImage());
            dispatch(cleanAlert());
        }
    };

    return (
        <Modal open={props.open} onClose={props.onClose} aria-labelledby='modal-product' aria-describedby='modal-product-description'>
            <Box mx={style}>
                <Draggable cancel='.btn'>
                    <Stack variant='modal' direction={'column'} spacing={1} padding={'1rem'}>
                        <Stack variant='bottomLine' direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                            <Typography variant='h5' gutterBottom color={variable.greenLight}>
                                {drainChoiceType[props.drainageTrenchType]}
                            </Typography>
                            <IconButton aria-label='close-modal' onClick={props.onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Stack>
                        <Stack variant='bottomLineGreen' direction={'row'} justifyContent={'space-between'} spacing={2}>
                            <Input disabled label={t('name')} value={currentProduct?.translations?.find(x => x.iso == getLanguage())?.name} />
                            <Input
                                isNumber
                                disabled
                                label={t(drainHeight, { unitOfMeasure: drainageTrenchPD[drainHeight as TInputDrainageTrench]?.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!] })}
                                value={currentProduct?.productPhysicalDatas![0].drainHeight}
                            />
                            <Input
                                isNumber
                                disabled
                                label={t(drainUnitWidth, { unitOfMeasure: drainageTrenchPD[drainHeight as TInputDrainageTrench]?.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!] })}
                                value={currentProduct?.productPhysicalDatas![0].drainUnitWidth}
                            />
                        </Stack>
                        <Stack direction={'row'} spacing={2} flexGrow={1}>
                            <Stack position={'relative'} width={'40%'}>
                                <Stack variant='scrollable'>
                                    <List>
                                        {baseProductPreview!
                                            .data!.filter(x => (user.data?.role != ERole.Standard ? x.status != EStatus.Deleted : x.status == EStatus.Public))
                                            .map((x, i) => (
                                                <ListItemButton key={x.name} className='btn' selected={selectedIndex === i} onClick={e => handleListItemClick(i, x.id)}>
                                                    <ListItemText primary={x.name} />
                                                    <ListItemIcon style={{ justifyContent: 'flex-end' }}>
                                                        <Stack style={{ width: '20px', height: '20px', backgroundColor: x.color, border: `1px solid ${variable.black}` }} />
                                                    </ListItemIcon>
                                                </ListItemButton>
                                            ))}
                                    </List>
                                </Stack>
                            </Stack>
                            <Stack flexGrow={1} width={'60%'}>
                                {isLoading && <CircularProgress />}
                                {!isLoading && drainHeightOpt?.length! > 0 && drainUnitWidthOpt?.length! > 0 && (
                                    <Stack flexGrow={1} spacing={2} justifyContent={'center'}>
                                        <Carousel
                                            indicators={baseProduct!.images?.length! > 1}
                                            navButtonsProps={{
                                                style: {
                                                    backgroundColor: variable.white,
                                                    color: variable.greenLight,
                                                    border: `0.1rem solid ${variable.greenLight}`,
                                                    borderRadius: 0,
                                                    height: '2rem',
                                                    width: '2rem',
                                                },
                                            }}>
                                            {baseProduct!.images?.map((item, i) => (
                                                <CardMedia key={item.id} height={180} width={300} component='img' image={item.imageUrl} alt={`image-${i}`} />
                                            ))}
                                        </Carousel>
                                        <Stack direction={'column'} className='btn'>
                                            <Stack direction={'row'} justifyContent={'space-between'} spacing={1}>
                                                <DropDown
                                                    label={t(drainHeight, {
                                                        unitOfMeasure: productPD[drainHeight as TInputProductPhysicalDatas].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
                                                    })}
                                                    name={drainHeight}
                                                    labelId={`${drainHeight}-label`}
                                                    id={drainHeight}
                                                    value={formik.values.drainHeight}
                                                    onChange={handleChangeDrainHeight}>
                                                    {drainHeightOpt!.map(x => (
                                                        <MenuItem key={x} value={x}>
                                                            {x}
                                                        </MenuItem>
                                                    ))}
                                                </DropDown>
                                                <DropDown
                                                    label={t(drainUnitWidth, {
                                                        unitOfMeasure: productPD[drainUnitWidth as TInputProductPhysicalDatas].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
                                                    })}
                                                    name={drainUnitWidth}
                                                    labelId={`${drainUnitWidth}-label`}
                                                    value={formik.values.drainUnitWidth}
                                                    id={drainUnitWidth}
                                                    onChange={handleChangeDrainUnitWidth}>
                                                    {drainUnitWidthOpt!.map(x => (
                                                        <MenuItem key={x} value={x}>
                                                            {x}
                                                        </MenuItem>
                                                    ))}
                                                </DropDown>
                                            </Stack>
                                            <Stack direction={'row'} justifyContent={'space-between'} spacing={1}>
                                                <Input
                                                    value={formik.values.drainUnitLength}
                                                    id={drainUnitLength}
                                                    isNumber
                                                    disabled
                                                    label={t(drainUnitLength, {
                                                        unitOfMeasure: productPD[drainUnitLength as TInputProductPhysicalDatas]?.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
                                                    })}
                                                    name={drainUnitLength}
                                                    onChange={handleChange}
                                                />
                                                <Input
                                                    value={formik.values.maxUnitaryDrainageCapacity}
                                                    id={maxUnitaryDrainageCapacity}
                                                    isNumber
                                                    disabled
                                                    label={t(maxUnitaryDrainageCapacity, {
                                                        unitOfMeasure: productPD[maxUnitaryDrainageCapacity as TInputProductPhysicalDatas]?.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
                                                    })}
                                                    name={maxUnitaryDrainageCapacity}
                                                    onChange={handleChange}
                                                    textTooltip={t('maxUnitaryDrainageCapacityInfobox')}
                                                />
                                            </Stack>
                                            <Stack direction={'row'} justifyContent={'space-between'} spacing={1}>
                                                <Input
                                                    value={formik.values.maximumAppliedLoad}
                                                    id={maximumAppliedLoad}
                                                    isNumber
                                                    disabled
                                                    label={t(maximumAppliedLoad, {
                                                        unitOfMeasure: productPD[maximumAppliedLoad as TInputProductPhysicalDatas]?.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
                                                    })}
                                                    name={maximumAppliedLoad}
                                                    onChange={handleChange}
                                                />
                                                <Input value={baseProduct!.hasPipe ? t('yes') : t('no')} id={hasDrainagePiping} disabled label={t(hasDrainagePiping)} />
                                            </Stack>
                                            <Stack direction={'row'} spacing={5} marginTop={5} justifyContent={'flex-end'}>
                                                <Button variant='white' onClick={props.onClose}>
                                                    {t('close')}
                                                </Button>
                                                <Button variant='orange' onClick={handleClickApply}>
                                                    {t('apply')}
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                )}
                            </Stack>
                        </Stack>
                    </Stack>
                </Draggable>
            </Box>
        </Modal>
    );
};

export default ModalProduct;
